import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, PaletteUtils } from '@shared/utils';
import { DurationType } from '@shared/api/models';
import { Tooltip } from '../tooltip';
import { ColorsEnum, Icon, NoData, Text } from '@shared/widgets';
import TransactionIcon from '@assets/icons/dashboard/transaction.svg';
import { TransactionDirection } from '@shared/api';

export interface IProps {
  amount: number;
  numberOfTransaction: number;
  highestTransferAmount: {
    date: string;
    amount: number;
  }[];
  recurrentRecipientsInfo: {
    recipientName: string;
    amount: number;
    numberOfTransaction: number;
  }[];
  textValue: string;
  currency: string;
  itemType: TransactionDirection;
  type?: DurationType;
}

export const TransactionItem = (props: IProps) => {
  const { t } = useTranslation('dashboard');

  const sortedValues = props?.highestTransferAmount ? [...props.highestTransferAmount] : [];
  const highestElement = sortedValues.sort((a, b) => b.amount - a.amount)?.[0]?.amount;
  return (
    <div className={cn('flex gap-6', 'max-[1250px]:flex-wrap', PaletteUtils.isVcfcPalette && ['mt-4 p-6', 'max-md:mt-0 max-md:p-4'])}>
      <div
        className={cn(
          'relative w-full',
          PaletteUtils.isInsigniaPalette && 'bg-white pl-6 pt-6 dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white p-6 dark:bg-light/5',
        )}
      >
        <div className='max-w-48'>
          <Text
            color={ColorsEnum.Primary}
            className={cn('mb-1', PaletteUtils.isVcfcPalette && 'font-[NewtonCCT] dark:text-white', PaletteUtils.isInsigniaPalette && 'font-[Prata]')}
            size='3xl'
          >
            {AmountUtils.transformToAmountWithCurrency(props.amount, props.currency)}
          </Text>
          <Text color={ColorsEnum.Secondary}>
            {t('payment.transaction.amountDuringPeriod', {
              itemType: t(`payment.transaction.type.${props.itemType.toLowerCase()}`),
              period: props.textValue,
            })}
          </Text>
        </div>
        <div className='mt-6 max-w-60'>
          <Text
            color={ColorsEnum.Primary}
            className={cn('mb-1', PaletteUtils.isVcfcPalette && 'font-[NewtonCCT] dark:text-white', PaletteUtils.isInsigniaPalette && 'font-[Prata]')}
            size='3xl'
          >
            {props.numberOfTransaction}
          </Text>
          <Text color={ColorsEnum.Secondary}>
            {t('payment.transaction.transactionsCountDuringPeriod', {
              itemType: t(`payment.transaction.type.${props.itemType.toLowerCase()}`).toLowerCase(),
              period: props.textValue,
            })}
          </Text>
        </div>
        {PaletteUtils.isInsigniaPalette && <Icon icon={<TransactionIcon />} className='flex w-full justify-end' color='primary' />}
      </div>
      <div
        className={cn(
          'flex w-full flex-col justify-between p-6',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white dark:bg-light/5',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
        )}
      >
        <Text weight='semibold'>
          {t('payment.transaction.top5DaysAmount', { itemType: t(`payment.transaction.type.${props.itemType.toLowerCase()}`).toLowerCase() })}
        </Text>
        {props.highestTransferAmount?.length > 0 ? (
          <div className='mx-auto'>
            <div className='mb-5 mt-3 flex items-end gap-2'>
              {props.highestTransferAmount.map((item, idx) => (
                <Tooltip key={idx} content={AmountUtils.transformToAmountWithCurrency(item.amount, props.currency)} direction='right'>
                  <div className='flex flex-col'>
                    <div
                      className={cn(
                        'max-h-[120px] w-12 rounded-md border',
                        highestElement === item.amount
                          ? PaletteUtils.isVcfcPalette && props.type === DurationType.Yearly
                            ? 'border-golden-olive bg-golden-olive'
                            : 'border-primary bg-primary dark:border-white dark:bg-white'
                          : PaletteUtils.isVcfcPalette && props.type === DurationType.Yearly
                            ? 'border-golden-olive'
                            : 'border-primary dark:border-white',
                      )}
                      style={{ height: `calc(120px * ${(item.amount / highestElement).toFixed(3)})` }}
                    />
                    <Text className={cn('mt-2 text-center dark:text-white/50', PaletteUtils.isVcfcPalette && '!text-slate')}>
                      {dayjs(item.date).format('DD.MM')}
                    </Text>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        ) : (
          <NoData className='m-auto' text={t('noData.noRecords', { ns: 'common' })} />
        )}
      </div>
      <div
        className={cn(
          'flex w-full flex-col p-6 pb-3',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white dark:bg-light/5',
        )}
      >
        <Text weight='semibold'>
          {t('payment.transaction.top5DaysRecurrent', {
            type: TransactionDirection.Outgoing === props.itemType ? t('payment.transaction.recipients') : t('payment.transaction.senders'),
          })}
        </Text>
        {props.recurrentRecipientsInfo?.length > 0 ? (
          props.recurrentRecipientsInfo?.map((element, idx) => (
            <div key={idx} className='mt-3 flex py-1'>
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full text-base font-semibold text-white',
                  PaletteUtils.isInsigniaPalette && 'bg-black',
                  PaletteUtils.isVcfcPalette && props.type === DurationType.Yearly ? 'bg-golden-olive' : 'bg-primary',
                )}
              >
                {element.recipientName?.[0]}
              </div>
              <div className='ml-2'>
                <Text
                  weight='semibold'
                  color={PaletteUtils.getColor(undefined, ColorsEnum.Primary)}
                  className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white')}
                >
                  {element.recipientName}
                </Text>
                <div className='flex gap-2'>
                  <Text weight='semibold' size='xs' color={ColorsEnum.Secondary} uppercase>
                    {AmountUtils.transformToAmountWithCurrency(element.amount, props.currency)}
                  </Text>
                  <Text weight='semibold' size='xs' color={ColorsEnum.Secondary} uppercase>
                    {t('payment.transaction.transactionCount', { count: element.numberOfTransaction })}
                  </Text>
                </div>
              </div>
            </div>
          ))
        ) : (
          <NoData className='m-auto' text={t('noData.noRecords', { ns: 'common' })} />
        )}
      </div>
    </div>
  );
};
